<template>
  <div
    class="card h-100 border-0 credit-card"
    :class="isDarkMode ? 'credit-card-dark' : 'credit-card-light'"
  >
    <div class="card-header pb-0 border-0">
      <div class="d-flex flex-wrap justify-content-between align-items-center gap-3">
        <div>
          <h5 class="credit-title mb-1">Historie kreditu</h5>
          <p class="credit-subtitle mb-0">Pohyby na herním kreditu</p>
        </div>
        <div class="credit-toolbar">
          <div class="balance-chip" :class="isDarkMode ? 'balance-chip-dark' : 'balance-chip-light'">
            <span class="balance-label">Kredit</span>
            <span class="balance-value">{{ formatNumber(paidCreditBalance) }}</span>
          </div>
          <div class="balance-chip balance-chip-bonus" :class="isDarkMode ? 'balance-chip-dark' : 'balance-chip-light'">
            <span class="balance-label">Včetně bonusu</span>
            <span class="balance-value">{{ formatNumber(creditBalance) }}</span>
          </div>
          <argon-button class="credit-toolbar-btn" @click="emit('credit-action', 'charge')" color="primary">
            <span class="btn-line-main">Dobít</span>
            <span class="btn-line-sub">kredit</span>
          </argon-button>
          <argon-button
            v-if="canWithdraw"
            class="credit-toolbar-btn"
            @click="emit('credit-action', 'withDraw')"
            variant="outline"
            color="primary"
          >
            <span class="btn-line-main">Vybrat</span>
            <span class="btn-line-sub">peníze</span>
          </argon-button>
        </div>
      </div>
      <div class="filter-row mt-3">
        <div class="filter-groups">
          <div class="filter-pills">
            <button type="button" class="filter-pill" :class="{ 'is-active': source === 'all' }" @click="source = 'all'">Vše</button>
            <button type="button" class="filter-pill filter-pill-bank" :class="{ 'is-active': source === 'bank' }" @click="source = 'bank'">Banka</button>
            <button type="button" class="filter-pill filter-pill-game" :class="{ 'is-active': source === 'game' }" @click="source = 'game'">Hry</button>
            <button type="button" class="filter-pill filter-pill-credit" :class="{ 'is-active': source === 'credit' }" @click="source = 'credit'">Kredit</button>
            <button type="button" class="filter-pill filter-pill-bonus" :class="{ 'is-active': source === 'bonus' }" @click="source = 'bonus'">Bonus</button>
          </div>
          <div class="filter-pills">
            <button type="button" class="filter-pill" :class="{ 'is-active': direction === 'all' }" @click="direction = 'all'">Vše</button>
            <button type="button" class="filter-pill" :class="{ 'is-active': direction === 'in' }" @click="direction = 'in'">Příchozí</button>
            <button type="button" class="filter-pill" :class="{ 'is-active': direction === 'out' }" @click="direction = 'out'">Odchozí</button>
          </div>
        </div>
        <div
          class="filter-pills filter-summary"
          :class="{
            'is-bonus': source === 'bonus',
            'is-bank': source === 'bank',
            'is-game': source === 'game',
            'is-credit': source === 'credit',
          }"
        >
          <span class="filter-pill">{{ summaryLabel }}</span>
          <span class="filter-pill is-active">{{ formatNumber(filteredSaldo) }}</span>
        </div>
      </div>
    </div>
    <div v-if="!existsTransactionData" class="card-body">
      <div class="d-flex flex-column justify-content-center align-items-center py-5">
        <fa-icon class="mb-3 text-primary" icon="fa-solid fa-money-bill-transfer" size="4x" />
        <h5 class="credit-title mb-0">Žádné transakce</h5>
      </div>
    </div>
    <div v-else class="card-body pt-3">
      <credit-transaction-table :rows="filteredTransactionData" :source="source" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import ArgonButton from "@/components/ArgonButton.vue";
import CreditTransactionTable from "./tables/CreditTransactionTable.vue";

const props = defineProps({
  creditBalance: Number,
  transactionData: Object
});
const emit = defineEmits(["credit-action"]);
const store = useStore();
const isDarkMode = computed(() => !!store.state.isDarkMode);

const source = ref("all");
const direction = ref("all");

const BANK_TYPES = new Set(["CT_IBA", "CT_OBA"]);
const GAME_TYPES = new Set(["CT_OG", "CT_CRG", "CT_CNG", "CBT_OG", "CBT_CNG"]);

const typeCode = (item) => String(item.CreditType || "").toUpperCase();

const isIncoming = (item) => item.ChangeType == 1;
const isOutgoing = (item) => item.ChangeType == -1;

const isBank = (item) => BANK_TYPES.has(typeCode(item));

const isGame = (item) => GAME_TYPES.has(typeCode(item));

const isBonus = (item) => String(item.CreditKind || "").toUpperCase() === "B";
const isCredit = (item) => String(item.CreditKind || "").toUpperCase() === "C";

const paidCreditBalance = computed(() => {
  const list = Array.isArray(props.transactionData) ? props.transactionData : [];
  return list.reduce((sum, item) => sum + (isCredit(item) ? (Number(item.CreditAmount) || 0) : 0), 0);
});

const canWithdraw = computed(() => (Number(paidCreditBalance.value) || 0) > 0.005);

const existsTransactionData = computed(() => Array.isArray(props.transactionData) && props.transactionData.length > 0);
const filteredTransactionData = computed(() => {
  const list = Array.isArray(props.transactionData) ? props.transactionData : [];
  return list.filter((item) => {
    if (source.value === "bank" && !isBank(item)) return false;
    if (source.value === "game" && !isGame(item)) return false;
    if (source.value === "credit" && !isCredit(item)) return false;
    if (source.value === "bonus" && !isBonus(item)) return false;
    if (direction.value === "in" && !isIncoming(item)) return false;
    if (direction.value === "out" && !isOutgoing(item)) return false;
    return true;
  });
});

const SOURCE_LABEL = {
  all: "Vše",
  bank: "Banka",
  game: "Hry",
  credit: "Kredit",
  bonus: "Bonus",
};

const summaryLabel = computed(() => {
  const src = SOURCE_LABEL[source.value] || "Vše";
  if (direction.value === "in") return `${src} příchozí`;
  if (direction.value === "out") return `${src} odchozí`;
  return source.value === "all" ? "Vše celkem" : `${src} vše`;
});

const filteredSaldo = computed(() => (
  filteredTransactionData.value.reduce((sum, item) => sum + (Number(item.CreditAmount) || 0), 0)
));

const formatNumber = (number) => store.getters.formatNumber(number);
</script>

<style scoped>
.credit-card {
  border-radius: 1rem !important;
  box-shadow: none !important;
}

.credit-card-light {
  background: #ffffff;
  border: 1px solid #E2E8F0 !important;
}

.credit-card-dark {
  background: #0F172A !important;
  border: 1px solid #334155 !important;
}

.credit-title {
  font-weight: 800;
  color: #0F172A;
}

.credit-card-dark .credit-title {
  color: #E2E8F0;
}

.credit-subtitle {
  font-size: 0.8rem;
  color: #64748B;
}

.credit-card-dark .credit-subtitle {
  color: #94A3B8;
}

.credit-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 0.5rem;
}

.credit-toolbar :deep(.credit-toolbar-btn) {
  height: auto;
  min-height: 100%;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-top: 0.15rem;
  padding-bottom: 0.15rem;
  line-height: 1.15;
  border-radius: 0.75rem;
}

.credit-toolbar .btn-line-main {
  font-weight: 800;
}

.credit-toolbar .btn-line-sub {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  opacity: 0.9;
}

.balance-chip {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  padding: 0.45rem 0.85rem;
  border-radius: 0.75rem;
}

.balance-chip-light {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
}

.balance-chip-dark {
  background: #1E293B;
  border: 1px solid #334155;
}

.balance-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #94A3B8;
}

.balance-value {
  font-size: 1.05rem;
  font-weight: 800;
  color: #0F172A;
}

.credit-card-dark .balance-value {
  color: #FFFFFF;
}

.balance-chip-bonus.balance-chip-light {
  background: #FEF3C7;
  border-color: #FCD34D;
}

.balance-chip-bonus .balance-label {
  color: #92400E;
}

.balance-chip-bonus .balance-value {
  color: #D97706;
}

.credit-card-dark .balance-chip-bonus {
  background: #3B2F14;
  border-color: #D97706;
}

.credit-card-dark .balance-chip-bonus .balance-label {
  color: #FCD34D;
}

.credit-card-dark .balance-chip-bonus .balance-value {
  color: #FBBF24;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem 1.25rem;
}

.filter-groups {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.filter-summary {
  margin-left: auto;
  flex-shrink: 0;
  cursor: default;
}

.filter-pills {
  display: flex;
  flex-wrap: nowrap;
  flex-shrink: 0;
  gap: 0.25rem;
  padding: 0.2rem;
  border-radius: 999px;
  background: #F1F5F9;
}

.credit-card-dark .filter-pills {
  background: #1E293B;
}

.filter-pill {
  border: 0;
  background: transparent;
  color: #64748B;
  font-size: 0.8rem;
  font-weight: 700;
  padding: 0.35rem 0.85rem;
  border-radius: 999px;
}

.filter-pill.is-active {
  background: #ffffff;
  color: #0F172A;
}

.credit-card-dark .filter-pill {
  color: #94A3B8;
}

.credit-card-dark .filter-pill.is-active {
  background: #334155;
  color: #E2E8F0;
}

.filter-summary .filter-pill {
  cursor: default;
}

.filter-pill-bank {
  color: #0284C7;
}

.filter-pill-bank.is-active {
  background: #38BDF8;
  color: #082F49;
}

.credit-card-dark .filter-pill-bank {
  color: #7DD3FC;
}

.credit-card-dark .filter-pill-bank.is-active {
  background: #0EA5E9;
  color: #F0F9FF;
}

.filter-summary.is-bank {
  background: #E0F2FE;
}

.filter-summary.is-bank .filter-pill {
  color: #0369A1;
}

.filter-summary.is-bank .filter-pill.is-active {
  background: #38BDF8;
  color: #082F49;
}

.credit-card-dark .filter-summary.is-bank {
  background: #0C4A6E;
}

.credit-card-dark .filter-summary.is-bank .filter-pill {
  color: #BAE6FD;
}

.credit-card-dark .filter-summary.is-bank .filter-pill.is-active {
  background: #0EA5E9;
  color: #F0F9FF;
}

.filter-pill-game {
  color: #FF6B21;
}

.filter-pill-game.is-active {
  background: #FF6B21;
  color: #FFFFFF;
}

.credit-card-dark .filter-pill-game {
  color: #FF8A4C;
}

.credit-card-dark .filter-pill-game.is-active {
  background: #FF8A4C;
  color: #0F172A;
}

.filter-summary.is-game {
  background: #FFEDD5;
}

.filter-summary.is-game .filter-pill {
  color: #C2410C;
}

.filter-summary.is-game .filter-pill.is-active {
  background: #FF6B21;
  color: #FFFFFF;
}

.credit-card-dark .filter-summary.is-game {
  background: #3B2113;
}

.credit-card-dark .filter-summary.is-game .filter-pill {
  color: #FF8A4C;
}

.credit-card-dark .filter-summary.is-game .filter-pill.is-active {
  background: #FF8A4C;
  color: #0F172A;
}

.filter-pill-credit {
  color: #475569;
  background: transparent;
}

.filter-pill-credit.is-active {
  background: #E2E8F0;
  color: #0F172A;
  box-shadow: none;
}

.credit-card-dark .filter-pill-credit {
  background: transparent;
  color: #FFFFFF;
}

.credit-card-dark .filter-pill-credit.is-active {
  background: #334155;
  color: #FFFFFF;
}

.filter-summary.is-credit {
  background: #F8FAFC;
}

.filter-summary.is-credit .filter-pill {
  color: #334155;
}

.filter-summary.is-credit .filter-pill.is-active {
  background: #FFFFFF;
  color: #0F172A;
  box-shadow: 0 0 0 1px #E2E8F0;
}

.credit-card-dark .filter-summary.is-credit {
  background: #334155;
}

.credit-card-dark .filter-summary.is-credit .filter-pill {
  color: #FFFFFF;
}

.credit-card-dark .filter-summary.is-credit .filter-pill.is-active {
  background: #FFFFFF;
  color: #0F172A;
}

.filter-pill-bonus {
  color: #B45309;
}

.filter-pill-bonus.is-active {
  background: #F59E0B;
  color: #FFFFFF;
}

.credit-card-dark .filter-pill-bonus {
  color: #FBBF24;
}

.credit-card-dark .filter-pill-bonus.is-active {
  background: #D97706;
  color: #FFF7ED;
}

.filter-summary.is-bonus {
  background: #FEF3C7;
}

.filter-summary.is-bonus .filter-pill {
  color: #B45309;
}

.filter-summary.is-bonus .filter-pill.is-active {
  background: #F59E0B;
  color: #FFFFFF;
}

.credit-card-dark .filter-summary.is-bonus {
  background: #3B2F14;
}

.credit-card-dark .filter-summary.is-bonus .filter-pill {
  color: #FBBF24;
}

.credit-card-dark .filter-summary.is-bonus .filter-pill.is-active {
  background: #D97706;
  color: #FFF7ED;
}

.credit-card-dark :deep(.card-header),
.credit-card-dark :deep(.card-body) {
  background: transparent !important;
}
</style>
